import { useId, useRef } from 'react';
import { useWatch, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { Score } from '@/api/interviews';
import { Textarea } from '@/components/ui/Textarea';
import { cn } from '@/lib/cn';
import { type } from '@/theme/typography';

import type { FillingInForm } from './fillingInFields';
import { QuestionRating } from './QuestionRating';
import { commentIsRequired, ratingChosen } from './scoreOptions';
import { ScoreScale } from './ScoreScale';
import { problemFieldId, problemMessageKey, type FieldProblem } from './submissionProblems';

interface Props {
  form: UseFormReturn<FillingInForm>;
  /** The score's place in the array the payload sent — the frozen bank order (ADR 0004). */
  index: number;
  /** The payload's own row: the wording as it was asked, and nothing this block writes. */
  score: Score;
  /**
   * The completeness gate's answer about **this** question, if the last submission named it (TASK-084).
   * `undefined` while nothing has been refused, which is the ordinary state of a form being filled in.
   */
  problem?: FieldProblem;
  /**
   * What this comment held **when that refusal arrived** — the message stands until the reader changes it.
   * See the note on `shownProblem` for why it is a snapshot and not the current value re-checked.
   */
  commentAtRefusal?: string;
}

/**
 * One question of §6.6: the wording, the scale, the comment — separated from its neighbours by a
 * **hairline and not a card** (§6.6), because thirty cards on one page is a wall and this is a surface
 * somebody sits in for an hour.
 *
 * ⚠️ **Feedback protection #1 — the question-text snapshot (ADR 0004).** The wording on this screen is
 * `question_text_snapshot` and **nothing else**. It is the text as it was when this interview was
 * arranged. The sentence went on: ~~*"and the client could not render the live wording even if it
 * tried: the only route that carries a question's current text is the bank, which is `Admin`-only
 * (`(401, 403, 403, 200, 403, 403)`) — a `Recruiter` conducting an interview is 403 there and an
 * `Interviewer` always is, and the vacancy catalogue refuses an `Interviewer` too."*~~ ⚠️ **That
 * premise is retracted twice over, and `fillingIn.test.tsx` has carried the retraction since
 * 2026-08-12 while this module went on asserting it.** (The strike and its correction sit in one
 * paragraph deliberately — a retraction written in the *next* one does not speak for this one.)
 * ADR 0018 **reversed** the `Admin`-only row (2026-08-11, all three groups answered `200`), and
 * ADR 0019 deleted the route that row was about (2026-09-24): a question's live wording is reached
 * through the set that owns it — `GET /api/v1/question-sets/` and then `…/{set_id}/` — which answers
 * all three groups with the **rows narrowed per reader**, and a set offered by a vacancy this
 * interviewer is assigned to is inside that narrowing. So nothing refuses this screen the live
 * wording, and *"render the current question instead of the snapshot"* (§7's predicted edit) is
 * **forbidden rather than unavailable** — which makes the guard *more* load-bearing than it was, not
 * less. What this block owes is not asking. `fillingIn.test.tsx` asserts both halves — the snapshot
 * byte for byte with the live wording seeded as a marker nowhere on screen, and **no request to
 * `/api/v1/roles/` and none to `/api/v1/question-sets/` at all**.
 *
 * ⚠️ **`question_has_been_edited` is read by §6.7 and deliberately not by this screen.** The flag is in
 * the payload (Sprint 10's TASK-074 found it with no screen reading it) and §6.7's *"This question has
 * since been edited."* is TASK-085's. Here it would answer a question nobody filling in an interview
 * can act on — the wording in front of them is the wording they must ask, whatever the bank now says —
 * and it would invite the one thing ADR 0004 exists to prevent: somebody deciding to answer the *new*
 * wording instead. The decision is written into `DESIGN.md` §6.6 rather than left implicit.
 *
 * ⚠️ **The eyebrow above the question renders the live question's category and difficulty**, which is what
 * §6.6 asks for, off the two fields backend TASK-105 added to `ScoreOut`. They are the **live** question's
 * while the wording below them is the frozen snapshot — `backend/CONTEXT.md`'s *Snapshot* decision 10, on
 * the argument that a re-categorised question does not change what a comment means and a rewritten one
 * does; the absence of `_snapshot` in their names is the API saying so.
 *
 * ⚠️ **This paragraph said *"No eyebrow above the question"* until 2026-08-11, and every clause of it was
 * false** — `ScoreOut` has carried both fields since TASK-105, §6.6's annotation was struck on 2026-07-31
 * (*"BUILT … by backend TASK-105"*), the Backlog item it cited is closed, and the `CONTEXT.md`
 * contradiction it reported was resolved in `CONTEXT.md`'s favour. It was written when the gap was real
 * and outlived it by two sprints, 105 lines above the JSX that renders the thing it denied. Found by
 * `code-reviewer` on Sprint 16's diff, in the file this sprint restructured — and the sprint had already
 * written *"the eyebrow above the wording … stays where it is"* into `DESIGN.md` §6.6 in the same pass,
 * which is the retracted-claim shape `CLAUDE.md` forbids, reproduced against a *shipped feature* rather
 * than against a design sentence.
 */
export function QuestionBlock({ form, index, score, problem, commentAtRefusal }: Props) {
  const { t } = useTranslation(['interviews', 'components']);

  const ids = useId();
  const questionId = `${ids}-question`;
  const noteId = `${ids}-note`;
  /**
   * ⚠️ **This one id is derived from the question and not generated**, unlike the two above: a 422 names
   * the offending score by `question_id`, so the field it is about has to be findable **from the refusal**
   * — which is what `problemFieldId` is (TASK-084, and the reasoning is in `submissionProblems.ts`).
   */
  const commentId = problemFieldId({ kind: 'score', questionId: score.question_id });
  const problemId = `${commentId}-problem`;

  /**
   * ⚠️ **`useWatch` and not `form.watch()`, and on the longest form in the product that is not a
   * detail.** `watch` subscribes where `useForm` was called, so every keystroke would re-render the
   * whole list; `useWatch` subscribes here, so it re-renders this block. The `value` has to be watched
   * at all because the `Textarea` grows itself from its own `value` (§4) — an uncontrolled one keeps its
   * three rows and hides what is being typed behind `overflow-hidden`, which is the defect TASK-078
   * measured in a browser.
   */
  const rating = useWatch({ control: form.control, name: `scores.${index}.rating` });
  const comment = useWatch({ control: form.control, name: `scores.${index}.comment` });

  const registered = form.register(`scores.${index}.comment`);

  /**
   * The element to move focus to when 1, 2 or 5 is chosen (§6.6: the requirement *"is revealed inline
   * and focuses the field"*). It is kept beside `react-hook-form`'s own ref rather than instead of it —
   * both are called, which is exactly the case `Textarea`'s auto-grow was broken for four sprints by
   * (TASK-078: a caller's ref replaced the primitive's, and jsdom computes no layout so nothing
   * noticed).
   */
  const commentField = useRef<HTMLTextAreaElement | null>(null);

  const needsComment = commentIsRequired(rating);

  /**
   * ⚠️ **A refused submission's message goes when the reader **changes the field**, and not when the
   * field starts to look satisfied.** The two differ, and the difference is a defect this rule was
   * rewritten to remove: a refusal is an answer about what the **server** holds, so a screen that hid it
   * as soon as the *local* value satisfied the local copy of the rule would drop the server's answer
   * exactly in the case worth rendering — the two disagreeing. The snapshot comes from above
   * (`FillingIn` takes `form.getValues()` at the moment of the refusal), so this comparison costs no
   * watch of its own and re-renders nothing extra: the block already watches these two fields.
   *
   * A reason the client cannot judge (`unknown` — a rule added on the backend) is treated no differently,
   * for the same reason: nobody but the next submission can say whether it is satisfied.
   */
  const shownProblem =
    problem !== undefined && commentAtRefusal !== undefined && comment === commentAtRefusal
      ? problem
      : undefined;

  /*
   * Joined without a `.filter`, deliberately: protection #2's guard forbids `filter`, `slice`, `sort` and
   * `splice` anywhere in this screen's source (`fillingIn.test.tsx`), because the one list here that must
   * never be narrowed is `scores` — and a blunt rule that also catches two element ids is the right trade.
   */
  const describedBy =
    [needsComment ? noteId : '', shownProblem ? problemId : ''].join(' ').trim() || undefined;

  return (
    <li className="py-6 first:pt-0">
      {/*
       * ⚠️ **The rating in the margin, and it is §6.7's decision written on this screen too** (§6.6,
       * *"The rating in the margin, and the answer told apart from the question"*, decided in TASK-118).
       * §6.6 and §6.7 are one surface a person moves through, so the value must not change character
       * between being written and being read: what a reader will meet as a mark against the question is
       * what the writer sees while choosing it, in the same place and the same role.
       *
       * ⚠️ **It is an echo of the control below and never a second control** — nothing in it is
       * clickable, and it is `aria-hidden` (`silent`), because the scale is labelled by this same
       * question and already announces the same value. Two voices for one number beside one question is
       * noise a screen reader cannot resolve, and §9's pair rule is met here by the group's own label.
       *
       * ⚠️ **Flex and never a grid**, which is load-bearing rather than a preference:
       * `fillingIn.test.tsx` walks this folder's source for a grid column class, because §10 asks for a
       * single column below `lg` and what guards it is that there is none to switch off. This is one
       * layout at every width — 48px of margin, and a question readable beside it on the narrowest width
       * §10 designs for.
       */}
      <div className="flex gap-5">
        <div className="w-12 shrink-0 pt-4">
          <QuestionRating value={ratingChosen(rating)} silent />
        </div>

        <div className="min-w-0 flex-1">
          {/*
            §6.6's eyebrow — *"category and difficulty in eyebrow type above"* the question — **and it is
            new rather than restored** (backend TASK-105). It was annotated in §6.6 as *unbuildable*, not
            skipped: `ScoreOut` carried neither field, and the only route that did is the `Admin`-only
            question bank, so asking for it would have put §8's Forbidden state in front of the two groups
            who conduct interviews. The payload carries both now, off the join the edited-flag already
            reads.

            ⚠️ **These two are the *live* question's, while the text below is the frozen snapshot** —
            `CONTEXT.md`'s Snapshot decision 10: a re-categorised question does not change what a comment
            means, a rewritten one does. The absence of `_snapshot` in their names is the API's way of
            saying so, and this is the one place in the product where a frozen value and two live ones
            about the same question sit in the same block.
          */}
          <p className={type.eyebrow}>
            {t(`components:category.${score.question_category}`)} ·{' '}
            {t(`components:difficulty.${score.question_difficulty}`)}
          </p>

          {/*
           * §6.6: question text at 15px. It is the thing being read out loud, so it reads like prose —
           * and it keeps **body weight** rather than being greyed to tell it from the answer: the wording
           * is the frozen record (ADR 0004) and `ink-500` would say it matters less, which is false. The
           * rejection is recorded in §6.7 rather than left as an absence.
           */}
          <p id={questionId} className="mt-1 text-[15px] leading-[1.6] text-ink-900">
            {score.question_text_snapshot}
          </p>

          <div className="mt-4">
            <ScoreScale
              labelledBy={questionId}
              value={rating}
              onValueChange={(next) => {
                form.setValue(`scores.${index}.rating`, next, { shouldDirty: true });
                /*
                 * ⚠️ **Revealed and focused, and blocking nothing.** Choosing 1, 2 or 5 shows the sentence
                 * and puts the cursor in the comment; choosing 3 or 4 takes the sentence away. Nothing is
                 * refused either way — no keystroke, and no save (§6.6: *"enforced on submit, not on every
                 * keystroke, so nobody is nagged mid-thought"*; the autosave accepts a 1 with no comment by
                 * design, ADR 0007). The gate that does refuse is TASK-084's.
                 */
                if (commentIsRequired(next)) commentField.current?.focus();
              }}
            />
          </div>

          {/*
           * ⚠️ **The provenance device** (§6.6, *"the answer told apart from the question"*): what the
           * interviewer writes sits behind a hairline with a small indent, and the frozen wording above
           * does not. Rules, not colour and not a box — §1's own vocabulary, and what it encodes is a
           * fact about the content (ADR 0004's snapshot is the record, this is the reading) rather than a
           * decoration.
           *
           * ⚠️ **It carries no claim about the question's history.** *"This question has since been
           * edited."* is §6.7's note and stays §6.7's alone: while filling in, the wording in front of
           * the interviewer is the wording they must ask whatever the bank now says, and stating
           * otherwise invites the one thing ADR 0004 exists to prevent. A rule down the side of the
           * answer says *this text has a different author from the text above it*, which is true of
           * every question here, edited or not.
           */}
          <div className="mt-4 flex flex-col gap-1 border-l border-rule pl-4">
            <label htmlFor={commentId} className="text-xs font-medium text-ink-700">
              {t('filling.comment')}
            </label>

            {/*
             * ⚠️ **Not `Field`'s `error` slot, and not `text-negative`.** The only place `Field` renders
             * below a control is the error, which sets `aria-invalid` — and nothing here is invalid: the
             * comment is required at *submission*, the draft saves without it, and telling a screen
             * reader the field is in error while a person is still thinking is the nagging §6.6 rules
             * out. It is a description of the field, so it is `aria-describedby`, in ink rather than red.
             */}
            {needsComment && (
              <p id={noteId} className={cn(type.metadata, 'text-ink-700')}>
                {t('filling.commentRequired')}
              </p>
            )}

            {/*
             * ⚠️ **The refusal's message, at the field it names, in red — and this one *is* an error.**
             * The note above is a description of a field somebody is still thinking about; this is the
             * answer to a submission that was refused, so it sets `aria-invalid` and reads as a problem.
             * It is deliberately **not** a toast and not an `alert()` (§4): the fix is here, and a
             * message that floats away from the field takes the fix with it.
             */}
            {shownProblem && (
              <p id={problemId} className={cn(type.metadata, 'text-negative')}>
                {t(problemMessageKey(shownProblem))}
              </p>
            )}

            <Textarea
              {...registered}
              id={commentId}
              value={comment}
              aria-invalid={shownProblem !== undefined || undefined}
              aria-describedby={describedBy}
              /*
               * After the spread, and it calls `react-hook-form`'s ref as well as keeping our own.
               * Dropping either half is a real defect with a precedent: without the primitive's own ref
               * the box never grows (TASK-078), and without `registered.ref` the form never sees it.
               */
              ref={(node) => {
                commentField.current = node;
                registered.ref(node);
              }}
            />
          </div>
        </div>
      </div>
    </li>
  );
}
